<template>
  <div id="challenger-initiate">
	<p class="initiate-challenge-intro">
		The final step is to run a 'challenge' which compares the new
		configuration with the currently active configuration to evalute
		where and how their outcomes differ before activating the new
		profile.
	</p>
	<v-alert
		class="info initiate-challenge-alert"
		border="left"
		colored-border
		icon="mdi-information-outline"
	>
		This process is for informational use only and will not impact
		claims or accounts in any way.
	</v-alert>
	<div class="initiate-challenge-grid">
		<label v-if="activeTenantId !== 'master'" class="challenge-grid-heading">Using Profile</label>
		<label v-else class="challenge-grid-master-heading">Using Tenant</label>
		<div v-if="challengerProfile.profileLabel && activeTenantId !== 'master'" class="challenge-grid-item">
			{{ challengerProfile.profileLabel }}
		</div>
		<div v-else-if="tenantList.length !== 0 && activeTenantId === 'master'" class="challenge-grid-master-item">
			<v-select v-model="selectedTenant" :items="tenantListLabels" outlined class="challenge-grid-item"></v-select>
		</div>
		<div v-else>
			<v-skeleton-loader type="chip"></v-skeleton-loader>
		</div>
		<label class="challenge-grid-heading compare-heading">Compare</label>
		<div v-if="challengerDateListLabels.length > 0" class="challenge-grid-compare">
			<v-select v-model="selectedDateRange" :items="challengerDateListLabels" outlined class="challenge-grid-item"></v-select>
			<div class="challenge-tooltip">
				<v-tooltip right content-class="challenge-tooltip-message">
					<template v-slot:activator="{ on, attrs }">
					<v-icon
						v-bind="attrs"
						v-on="on"
					>
						mdi-information-outline
					</v-icon>
					</template>
					<span>If more than one profile was active during this time,
						ARIA will only use disputes from the selected profile.</span>
				</v-tooltip>
			</div>
		</div>
		<div v-else>
			<v-skeleton-loader type="chip"></v-skeleton-loader>
		</div>
	</div>
	<div v-if="disputeCount === 0" class="dispute-count">
		<v-icon>mdi-alert-outline</v-icon>
		<template>No disputes found</template>
	</div>
	<div v-else-if="disputeCount === 1" class="dispute-count">{{ disputeCount }} dispute</div>
	<div v-else-if="disputeCount !== ''" class="dispute-count">{{ disputeCount }} disputes</div>
	<div v-else>
		<v-skeleton-loader type="chip" class="dispute-count dispute-count-loader"></v-skeleton-loader>
	</div>
	<div class="initiate-challenge-run">
		<v-btn
		class="primary"
		:loading="initiateInprogress"
		@click="runChallenger()"
		:disabled="!allDataLoaded">
		Run Review Challenge
		</v-btn>
	</div>
	<modal-dialog
		title="Empty Challenge"
		executeText="Save &amp; Activate Anyway"
		:executeAction="onDeploy"
		:triggerCounter="activateProfileDialogCounter"
		:useActivator="false"
	>
		<div slot="content">
			<v-alert
				class="warning"
				border="left"
				colored-border
				icon="mdi-alert-rhombus"
			>
				We cannot perform a challenge.
				There are no disputes for the selected profile and date range.
			</v-alert>
		</div>
	</modal-dialog>
  </div>
</template>

<script>
	/**
	 * @fileoverview challenger-initiate.vue is the first of three components used for the Champion Challenger. It allows the user
	 * to select their date range and run the challenger. It relies on vuex to map the state variables from the store files.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from 'vuex';

	export default {
		data() {
			return {
				initiateInprogress: false,	// true when initiating challenger is in process
				deployReady: false,	// true when profile is ready to be deloyed
				activateProfileDialogCounter: 0,	// dialog counter to display the activate profile dialog box
				allDataLoaded: false	// true if all the data is loaded for the page
			}
		},
		props: {
			/**
			 * Original profile used for the champion challenger comparison
			 */
			challengerProfile: {
				required: true,
				type: Object
			},
			/**
			 * Current active tenantId used to initiate champion challenger
			 */
			activeTenantId: {
				required: true,
				type: String
			},
			/**
			 * TenantId selected for the champion challenger comparison in the admin view
			 */
			challengerTenantId: {
				required: true,
				type: String
			}
		},
		computed: {
			...mapState({
				tenantList: (state) => state.account.tenantList,
				challengerDateList: (state) => state.config.challengerDateList,
				challengerDateListLabels: (state) => state.config.challengerDateListLabels,
				challengerRange: (state) => state.config.challengerRange,
				disputeCount: (state) => state.config.disputeCount,
				editedProfile: (state) => state.config.configProfile,
				challengerStartDate: (state) => state.config.challengerStartDate,
				challengerEndDate: (state) => state.config.challengerEndDate,
			}),
			/**
			 * Getter and setter for the selected tenantId 
			 * For admin use only
			 */
			selectedTenant: {
				get() {
					if (this.activeTenantId === 'master'){
						if (this.challengerTenantId !== "") {
							return this.tenantList.find(tenant => tenant.tenantId === this.challengerTenantId).tenantLabel;
						}

						this.$store.dispatch("setChallengerTenantId", this.tenantList[0].tenantId);
						return this.tenantList[0].tenantLabel;
					}
					return null;
				},
				set(val) {
					this.$store.dispatch("setChallengerTenantId", this.tenantList.find(tenant => tenant.tenantLabel === val).tenantId);
				}
			},
			/**
			 * Getter and setter for the selected date range
			 */
			selectedDateRange: {
				get() {
					if (this.challengerRange !== "") {
						return this.challengerDateList.find(r => r.value === this.challengerRange).label;
					}
					return "";
				},
				set(val) {
					this.$store.dispatch("setChallengerRange", this.challengerDateList.find(date => date.label === val).value);
				}
			},
			/**
			 * Create a list of the tenant labels
			 * @returns array of tenant labels
			 */
			tenantListLabels() {
				let labels = [];
				this.tenantList.map(tenant => {
					labels.push(tenant.tenantLabel);
				});

				return labels;
			},
		},
		/**
		 * Mounted lifecycle hook
		 * Set flags and retrieve the dispute count
		 */
		mounted() {
			this.initiateInprogress = false;
			this.deployReady = true;

			this.checkDataLoaded();

			if (this.disputeCount === "" && this.challengerStartDate !== "" && this.challengerProfile.profileId !== undefined) {
				this.retrieveDisputeCount();
			}
		},
		watch: {
			/**
			 * Set deployReady flag in the config store
			 */
			deployReady() {
				this.$store.dispatch("setAllowProfileDeploy", this.deployReady);
			},
			/**
			 * Retrieve the dispute count when the challenger profile changes
			 */
			challengerProfile() {
				this.retrieveDisputeCount();
			},
			/**
			 * Retrieve the dispute count when the challenger start date changes and the challengerProfile is loaded
			 */
			challengerStartDate() {
				if (this.challengerProfile.profileId !== undefined){
					this.retrieveDisputeCount();
				}
			},
			/**
			 * Check if the data is loaded when the dispute count changes
			 */
			disputeCount() {
				this.checkDataLoaded();
			},
			/**
			 * Check if the data is loaded when the selected date range changes
			 */
			selectedDateRange() {
				this.checkDataLoaded();
			},
			/**
			 * Set the allDataLoaded flag to false when the selected tenant changes
			 */
			selectedTenant() {
				this.allDataLoaded = false;
			}
		},
		methods: {
			/**
			 * Clear the dispute count for the skeleton loader to appear
			 * Retrieve the dispute count based on the selected data
			 */
			async retrieveDisputeCount() {
				await this.$store.dispatch("clearDisputeCount");

				let payload = {
					tenantId: this.activeTenantId,
					profileId: this.challengerProfile.profileId,
					startDate: this.challengerStartDate,
					endDate: this.challengerEndDate
				};

				// If activeTenantId === 'master'
				// Send challengerTenantId
				if (this.activeTenantId === 'master') {
					payload.tenantId = this.challengerTenantId;
				}

				this.$store.dispatch("retrieveDisputeCount", payload);
			},
			/**
			 * If there are disputes available, run CC with the selected data
			 * Otherwise, display the 'activate profile dialog box' for a direct deploy
			 */
			runChallenger() {
				if (this.disputeCount !== 0) {
					this.initiateInprogress = true;
					let payload = {
						tenantId: this.activeTenantId,
						initiatedFor: this.activeTenantId,
						profileIdNew: this.editedProfile.profileId,
						startDate: this.challengerStartDate,
						endDate: this.challengerEndDate
					};

					// If activeTenantId === 'master'
					// Send challengerTenantId in for the tenantId
					if (this.activeTenantId === 'master') {
						payload.tenantId = this.challengerTenantId;
					} 

					this.$store.dispatch("initiateChampionChallenger", payload);
				} 
				else {
					this.activateProfileDialogCounter += 1;
				}
			},
			/**
			 * Set the allDataLoaded flag based on the disupteCount and selectedDateRange
			 */
			checkDataLoaded() {
				let loaded = false;
				if (this.disputeCount !== "" && this.selectedDateRange !== "") {
					loaded = true;
				}
			
				this.allDataLoaded = loaded;
			},
			/**
			 * Emit that deploy was selected to the parent
			 */
			onDeploy() {
				this.$emit("deploy");
			},
		}
	}
</script>

<style>

</style>